import { useRef, useState } from 'react';
import IconV2 from '@/components/v2/IconV2';
import { FiExternalLink, FiEdit2, FiPlus } from '@/components/react-icons/main/index.esm';
import sendToast from '@/lib/sendToast';

const FileCell = ({ row, column, value }) => {
  const inputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);

  const { fileLabel = 'File', accept, allowedMimeTypes, maxSizeMb, onChange, onView, addTooltip, viewTooltip, replaceTooltip, cellClassName } = column;

  const resetInput = () => {
    if (!inputRef.current) {
      return;
    }

    inputRef.current.value = '';
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    const label = fileLabel || 'file';
    const labelLower = label.toLowerCase();

    if (Array.isArray(allowedMimeTypes) && allowedMimeTypes.length > 0 && !allowedMimeTypes.includes(file.type)) {
      sendToast({
        title: 'Invalid file type',
        description: `Please upload a valid ${labelLower}.`,
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      resetInput();
      return;
    }

    if (maxSizeMb && file.size > maxSizeMb * 1024 * 1024) {
      sendToast({
        title: 'File too large',
        description: `File size should be less than ${maxSizeMb}MB.`,
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      resetInput();
      return;
    }

    setIsUploading(true);

    try {
      if (onChange) {
        await onChange(file, { row, column });
      }

      sendToast({
        title: 'Success',
        message: `${label} uploaded successfully`,
        status: 'success',
        duration: 2000,
        isClosable: true
      });
    } catch (error) {
      sendToast({
        title: 'Upload failed',
        message: error?.message || `Failed to upload ${labelLower}.`,
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    } finally {
      setIsUploading(false);
      resetInput();
    }
  };

  const handleOpenPicker = () => {
    if (isUploading) {
      return;
    }

    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const baseStyle = {
    opacity: isUploading ? 0.5 : 1,
    cursor: isUploading ? 'not-allowed' : 'pointer'
  };

  const label = fileLabel || 'File';

  return (
    <div className={`comp-grid-table-cell file-cell ${cellClassName || ''}`}>
      <input ref={inputRef} type="file" onChange={handleFileSelect} style={{ display: 'none' }} accept={accept} disabled={isUploading} />
      {value ? (
        <>
          {onView && (
            <div
              role="button"
              className="action-button"
              onClick={(e) => {
                e.stopPropagation();

                if (!isUploading) {
                  onView(value, { row, column });
                }
              }}
              data-title={viewTooltip || `View ${label}`}
              style={baseStyle}>
              <IconV2 icon={FiExternalLink} />
            </div>
          )}
          <div
            role="button"
            className="action-button"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenPicker();
            }}
            data-title={replaceTooltip || `Replace ${label}`}
            style={baseStyle}>
            <IconV2 icon={FiEdit2} />
          </div>
        </>
      ) : (
        <div
          role="button"
          className="action-button"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenPicker();
          }}
          data-title={addTooltip || `Add ${label}`}
          style={baseStyle}>
          <IconV2 icon={FiPlus} />
        </div>
      )}
    </div>
  );
};

export default FileCell;
